import {memo} from 'react';
import {DepositRow} from './DepositRow';
import {ServiceRow} from './ServiceRow';
import {ReservationRoomRow} from './ReservationRoomRow';
import {currencyFormat, translate} from '../../utils';

export const ItemsTable = memo(({collection, alreadyInvoicedItems, toggleSelection}) => {
    let reservationRooms = {};
    let reservationServices = [];
    const deposits = [];
    for (const [type, item] of collection.items) {
        if (type === 'reservationRoom') {
            reservationRooms[item.id] = reservationRooms[item.id] || {
                id: item.id,
                reservationRoom: null,
                services: [],
            };
            reservationRooms[item.id].reservationRoom = item;
        } else if (type === 'service') {
            if (item.reference.reservationRoom) {
                reservationRooms[item.reference.reservationRoom.id] = reservationRooms[item.reference.reservationRoom.id] || {
                    id: item.reference.reservationRoom.id,
                    reservationRoom: null,
                    services: [],
                };
                reservationRooms[item.reference.reservationRoom.id].services.push(item);
            } else {
                reservationServices.push(item);
            }
        } else if (type === 'deposit') {
            deposits.push(item);
        }
    }
    reservationRooms = Object.values(reservationRooms);

    const [, lastRoomish] = firstLast(collection.items, element => {
        return element[0] === 'reservationRoom' || (element[0] === 'service' && element[1].reference.reservationRoom);
    });
    let lastRoomId = null;
    if (lastRoomish && lastRoomish[0] === 'reservationRoom') {
        lastRoomId = lastRoomish[1].id;
    } else if (lastRoomish && lastRoomish[0] === 'service') {
        lastRoomId = lastRoomish[1].reference.reservationRoom.id;
    }

    const rows = [];
    for (const {id, reservationRoom, services} of reservationRooms) {
        if (rows.length > 0) {
            rows.push(
                <tr key={`divider_${id}`} className="reservation-rooms__rooms-divider">
                    <td colSpan="6">
                        <div/>
                    </td>
                </tr>,
            );
        }

        const isLast = id === lastRoomId;

        if (reservationRoom) {
            rows.push(
                <ReservationRoomRow key={`room_${reservationRoom.id}`} reservationRoom={reservationRoom} hasServices={services.length > 0} isLast={isLast} isSelected={collection.isSelected(reservationRoom)} onClick={() => toggleSelection(collection, reservationRoom)} isInvoiced={alreadyInvoicedItems.reservationRooms.has(id)}/>,
            );
        }

        if (services.length > 0) {
            const servicesFirst = services[0];
            const servicesLast = services[services.length - 1];
            rows.push(
                ...services.map(service => (
                    <ServiceRow key={`service_${service.id}`} service={service} isLastRoom={isLast} isFirst={service === servicesFirst} isLast={service === servicesLast} isSelected={collection.isSelected(service)} onClick={() => toggleSelection(collection, service)} isInvoiced={alreadyInvoicedItems.services.has(service.id)}/>
                )),
            );
        }
    }

    const [firstReservationService, lastReservationService] = firstLast(collection.items, element => element[0] === 'service' && !element[1].reference.reservationRoom);
    const serviceRows = [];
    for (const service of reservationServices) {
        const isFirst = service === firstReservationService[1];
        const isLast = service === lastReservationService[1];
        serviceRows.push(
            <ServiceRow key={`service_${service.id}`} service={service} isReservation={true} isFirst={isFirst} isLast={isLast} isSelected={collection.isSelected(service)} onClick={() => toggleSelection(collection, service)} isInvoiced={alreadyInvoicedItems.services.has(service.id)}/>,
        );
    }

    if (serviceRows.length > 0) {
        if (rows.length > 0) {
            rows.push(
                <tr key={'pre_services_spacer'} className="reservation-rooms__spacer">
                    <td colSpan="6"/>
                </tr>,
            );
        }
        rows.push(...serviceRows);
    }

    const [firstDeposit, lastDeposit] = firstLast(collection.items, element => element[0] === 'deposit');
    for (const deposit of deposits) {
        const isFirst = deposit === firstDeposit[1];
        const isLast = deposit === lastDeposit[1];
        if (isFirst && rows.length) {
            rows.push(
                <tr key={'pre_deposits_spacer'} className="reservation-rooms__spacer">
                    <td colSpan="6"/>
                </tr>,
            );
        }
        rows.push(
            <DepositRow key={`deposit_${deposit.id}`} deposit={deposit} isFirst={isFirst} isLast={isLast} isSelected={collection.isSelected(deposit)} onClick={() => toggleSelection(collection, deposit)} isInvoiced={alreadyInvoicedItems.deposits.has(deposit.id)}/>,
        );
    }

    return <div className="table-responsive reservation-rooms">
        <table className="table table-wrap font-size-12">
            <thead>
            <tr>
                <th className="col-xs-1">
                    {translate('frontend', 'room')}
                </th>
                <th className="col-xs-2">
                    {translate('frontend', 'category')}
                </th>
                <th className="col-xs-3">
                    {translate('frontend', 'Duration')}
                </th>
                <th colSpan="2" className="col-xs-4">
                    {translate('frontend', 'Guest Name')}
                </th>
                <th className="col-xs-2 text-right">
                    {translate('frontend', 'AMOUNT')}
                </th>
            </tr>
            </thead>
            <tbody className="RlistTableContent">{rows}</tbody>
            {rows.length > 0 ? <tfoot className="footer-without-borders">
            <tr>
                <th colSpan="5" className="no-br text-right">{translate('frontend', 'TOT_GRD')}</th>
                <th className="no-br text-right">{currencyFormat(collection.total)}</th>
            </tr>
            </tfoot> : null}
        </table>
    </div>
});
ItemsTable.displayName = 'ItemsTable';

function firstLast(items, predicate) {
    return items.reduce((carry, element) => {
        if (predicate(element)) {
            return [carry[0] || element, element];
        } else {
            return carry;
        }
    }, [null, null]);
}
